// src/styles/global.js
import { createGlobalStyle } from 'styled-components';
import 'overlayscrollbars/overlayscrollbars.css';

const GlobalStyle = createGlobalStyle`
  /* Font imports */
  @font-face {
    font-family: 'Space Grotesk';
    src: url('/fonts/space-grotesk-v16-latin-regular.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Space Grotesk';
    src: url('/fonts/space-grotesk-v16-latin-500.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Space Grotesk';
    src: url('/fonts/space-grotesk-v16-latin-600.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Space Grotesk';
    src: url('/fonts/space-grotesk-v16-latin-700.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  /* CSS Variables for theme values */
  :root {
    --bg: ${({ theme }) => theme.colors.bg};
    --card: ${({ theme }) => theme.colors.card};
    --secondary: ${({ theme }) => theme.colors.secondary};
    --text: ${({ theme }) => theme.colors.text};
    --text-secondary: ${({ theme }) => theme.colors.textSecondary};
    --accent: ${({ theme }) => theme.colors.accent};
    
    /* CSS Variables for spacing */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-12: 3rem;
    --space-16: 4rem;
    --space-24: 6rem;
    
    /* CSS Variables for fonts */
    --font-sans: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    
    /* CSS Variables for shadows */
    --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.1);
    --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
    --shadow-xl: 0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04);
  }

  /* Box sizing rules */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Set base font size for rem calculations */
  html {
    font-size: 16px;
    scroll-behavior: smooth;
    
    /* Hide native scrollbars */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }

  /* Body base styling */
  body {
    font-family: var(--font-sans);
    background: var(--bg);
    color: var(--text);
    line-height: 1.6;
    overflow-x: hidden;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    transition: background-color 0.3s ease, color 0.3s ease;
    
    /* Ensure body doesn't create scrollbars when using custom scrollbar */
    &.custom-scrollbar-active {
      overflow: hidden;
      height: 100vh;
    }
  }

  /* Improved accessibility for interactive elements on mobile */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    button, 
    a,
    input[type="button"],
    input[type="submit"] {
      min-height: 44px;
      min-width: 44px;
    }
  }

  /* Focus styles for better accessibility */
  :focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  /* Reduced motion for users who prefer it */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Link styling */
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
  }

  /* Button styling */
  button {
    font-family: inherit;
    cursor: pointer;
  }

  /* Image styling */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Form element styling */
  input,
  textarea,
  select {
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    border: 1px solid var(--secondary);
    border-radius: 8px;
    background: transparent;

    &:focus {
      outline: none;
      border-color: var(--accent);
    }
  }

  /* Apply transition to specific elements only */
  .theme-transition {
    transition: background-color 0.3s ease,
                border-color 0.3s ease,
                color 0.3s ease;
  }

  /* Container utility classes */
  .container {
    width: 95%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 1rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      width: 100%;
      padding: 0 1rem;
    }
  }
  
  /* Section utility classes */
  section {
    padding: 4rem 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      padding: 2rem 0;
    }
  }
  
  /* Animation helper classes */
  .will-animate {
    will-change: transform, opacity;
  }
  
  .animate-fade-in {
    animation: fadeIn 0.5s forwards;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .animate-slide-in {
    animation: slideIn 0.5s forwards;
  }
  
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
  }
  
  /* Utility class to hide content while keeping it accessible to screen readers */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
  
  /* Code block styling */
  code {
    font-family: 'Fira Code', monospace;
    font-size: 0.9em;
    background: ${({ theme }) => theme.colors.secondary};
    padding: 0.2em 0.4em;
    border-radius: 4px;
  }
  
  pre {
    background: ${({ theme }) => theme.colors.card};
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    font-family: 'Fira Code', monospace;
    font-size: 0.9em;
    border: 1px solid ${({ theme }) => theme.colors.secondary};
  }
  
  /* Improved scroll behavior */
  .smooth-scroll {
    scroll-behavior: smooth;
  }
  
  /* Custom scrollbar related styles */
  .os-environment {
    height: 100vh !important;
  }
  
  /* Ensure nothing creates unexpected scrollbars */
  .os-environment, .os-viewport, .os-content {
    overflow: visible !important;
  }
`;

export default GlobalStyle;